// src/hooks/useDrupalFileUpload.ts
// Hook for uploading files to CMS via the EM3 backend proxy.
// The backend handles auth, validation, and preprocessing.

import { useFusionAuth } from '@fusionauth/react-sdk';

interface FileMetadata {
  fileTypeId?: string;
  wildfireId?: string;
  subcategoryId?: string;
  regionId?: string;
  fileDate?: string;
  description?: string;
  fireReference?: string;
  varId?: string;
  // Perimeter shapefile dispatch: when both are present and the subcategory
  // resolves to the perimeter term, the backend runs the perimeter pipeline.
  perimeterSource?: string;
  fire_number?: string;
}

interface UploadResult {
  success: boolean;
  node?: { id: number; uuid: string; title: string };
  file?: {
    uuid: string;
    url: string;
    name: string;
    mimeType: string;
  };
  error?: string;
  code?: string;
  message?: string;
}

// If the access-token cookie has less than this remaining when an upload
// starts, refresh first. The 60s JWT (project_fa_jwt_ttl_60s_intent) plus
// high-latency links (Starlink in NWT communities) means a token can lapse
// between SDK auto-refresh and the user clicking Upload — issue #669.
const REFRESH_BEFORE_UPLOAD_MS = 20_000;

function getAtExpRemainingMs(): number {
  const raw = document.cookie
    .split('; ')
    .find((c) => c.startsWith('app.at_exp='))
    ?.split('=')[1];
  const expMs = raw ? parseInt(raw, 10) * 1000 : 0;
  return expMs - Date.now();
}

export function useDrupalFileUpload() {
  const { refreshToken } = useFusionAuth() as {
    refreshToken: () => Promise<Response | undefined>;
  };

  const uploadFile = async (
    file: File,
    metadata: FileMetadata,
    contentType = 'fire_file',
  ): Promise<UploadResult> => {
    // Pre-flight: if the JWT cookie is about to expire (or already has),
    // refresh before sending. The SDK's fetch is wrapped by refreshCoordinator
    // so cross-tab races are handled. Failures fall through — the upload will
    // still attempt and the server's existing 401 surfaces normally.
    if (getAtExpRemainingMs() < REFRESH_BEFORE_UPLOAD_MS) {
      try {
        await refreshToken();
      } catch (err) {
        console.warn('[useDrupalFileUpload] pre-flight refresh failed:', err);
      }
    }

    const formData = new FormData();
    formData.append('file', file);
    formData.append('metadata', JSON.stringify(metadata));

    const res = await fetch(`/api/file-upload/${contentType}`, {
      method: 'POST',
      credentials: 'include', // sends app.at httpOnly cookie
      body: formData,         // browser sets Content-Type with boundary
    });

    return res.json();
  };

  const uploadFiles = async (
    files: File[],
    metadata: FileMetadata,
    contentType = 'fire_file',
  ): Promise<UploadResult[]> => {
    const results: UploadResult[] = [];
    for (const file of files) {
      try {
        const result = await uploadFile(file, { ...metadata, fileName: file.name } as any, contentType);
        results.push(result);
      } catch (error) {
        results.push({
          success: false,
          error: error instanceof Error ? error.message : 'Unknown error',
        });
      }
    }
    return results;
  };

  return { uploadFile, uploadFiles };
}
